import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from 'reactstrap';
import Select from 'react-select';
import SweetAlert from 'react-bootstrap-sweetalert';

import { activateAuthLayout } from '../../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { capitalizeStr, getProductImageUrl, roundCurrency } from '../../../../helpers/common';
import ColumnHeadersTooltip from '../../../../components/ColumnHeadersTooltip';
import ImageDropzone from '../../../../components/ImageDropzone';

const GET_DATA = gql`
  query getData($ProductId: Int) {
    Product(ProductId: $ProductId) {
      ProductId
      Name
      CatId
      Active
      ProductCategory {
        CatId
        Name
      }
      AttributeValues {
        AttributeValueId
        AttributeId
        Value
        Attribute {
          Name
        }
      }
      SyncWithXero
      XeroTrackedAsInventory
      ProductCode
      XeroInventoryAssetAccountCode
      XeroCOA
      XeroAccount {
        Account
      }
      XeroCOASales
      XeroAccountSales {
        Account
      }
      ProductPriceLines {
        PriceLineId
        PriceType
        Description
        ProductCode
        CostPrice
        Multiplier
        AllowCostPriceOverride
        OpexProration
        LinkQuoteItems
        ShowInHardwareTable
        ProductPrices {
          ProductPriceId
          QuoteTypeId
          Price
          ShowPrice
          DisplayProduct
          IncludePriceInTotal
        }
      }
      ProductRates {
        RateId
        RateLineId
        ProductId
        RateType
        RateUnits
        Rate
        QuoteTypeId
      }
      CAFTemplates {
        CAFTemplateId
        Name
        CAFSectionId
        CAFSection {
          Name
        }
      }
      ProvisioningTypeId
      ProvisioningType {
        ProvisioningTypeId
        Handle
        Name
      }
      HasProposalTemplate
      ProposalContent
      ProposalLink
      Image
      Companions {
        CompanionProductId
        CompanionId
        Quantity
        LinkQuoteItems
        CompanionProduct {
          Name
          ProductCode
        }
      }
    }

    ProductCategories {
      CatId
      Name
    }

    Attributes {
      AttributeId
      Handle
      Name
      Values {
        AttributeValueId
        Value
      }
    }

    ProvisioningTypes {
      ProvisioningTypeId
      Handle
      Name
    }

    Products {
      ProductId
      Name
      ProductCode
    }

    XeroCOA {
      XeroCOAId
      Account
    }

    QuoteTypes {
      QuoteTypeId
      Name
      Description
      DefaultMultiplier
      Active
    }

    CAFTemplates {
      CAFTemplateId
      Name
      CAFSectionId
      CAFSection {
        Name
      }
    }
  }
`;

const CREATE_PRODUCT = gql`
  mutation createProduct($ProductInput: ProductInput) {
    createProduct(input: $ProductInput) {
      ProductId
      Name
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation updateProduct($ProductInput: ProductInput) {
    updateProduct(input: $ProductInput) {
      ProductId
      ProductCode
      Name
      ProductCategory {
        CatId
        Name
      }
      ProvisioningTypeId
      SyncWithXero
      XeroTrackedAsInventory
      HasProposalTemplate
      ProposalContent
      ProposalLink
      Image
      ProductPriceLines {
        PriceLineId
        PriceType
        CostPrice
        Multiplier
        AllowCostPriceOverride
        OpexProration
        LinkQuoteItems
        ShowInHardwareTable
        ProductPrices {
          ProductPriceId
          QuoteTypeId
          Price
          ShowPrice
          DisplayProduct
          IncludePriceInTotal
        }
      }
    }
  }
`;

const CREATE_PRODUCT_RATES = gql`
  mutation createProductRates(
    $ProductRatesList: [ProductRatesList]
    $ProductId: Int
  ) {
    createProductRates(input: $ProductRatesList, ProductId: $ProductId) {
      RateId
      ProductId
      RateType
      RateUnits
      Rate
      QuoteTypeId
    }
  }
`;

const SYNC_ATTRIBUTE_VALUES = gql`
  mutation syncProductAttributeValues($ProductId: Int!, $AttributeValueIds: [Int!]!) {
    syncProductAttributeValues(ProductId: $ProductId, AttributeValueIds: $AttributeValueIds) {
      AttributeValues {
        AttributeValueId
        AttributeId
        Value
        Attribute { Name }
      }
    }
  }
`;

const SYNC_PRODUCT_CAF_TEMPLATES = gql`
  mutation syncProductCAFTemplates($ProductId: Int!, $CAFTemplateIds: [Int]!) {
    syncProductCAFTemplates(ProductId: $ProductId, CAFTemplateIds: $CAFTemplateIds) {
      ProductId
    }
  }
`;

const UPLOAD_PRODUCT_IMAGE = gql`
  mutation uploadProductImage($productId: ID!, $file: Upload!) {
    uploadProductImage(productId: $productId, file: $file)
  }
`;

const CREATE_PRODUCT_PRICE_LINES = gql`
  mutation createProductPriceLines(
    $ProductPriceLineInput: [ProductPriceLineInput]
    $ProductId: Int
  ) {
    createProductPriceLines(input: $ProductPriceLineInput, ProductId: $ProductId) {
      PriceLineId
      PriceType
      Description
      ProductCode
      CostPrice
      Multiplier
      AllowCostPriceOverride
      OpexProration
      LinkQuoteItems
      ProductPrices {
        ProductPriceId
        QuoteTypeId
        Price
        ShowPrice
        DisplayProduct
        IncludePriceInTotal
      }
    }
  }
`;

const SYNC_COMPANION_PRODUCTS = gql`
  mutation syncCompanionProducts($ProductId: Int!, $input: [CompanionProductInput]!) {
    syncCompanionProducts(ProductId: $ProductId, input: $input) {
      CompanionProductId
      CompanionId
      Quantity
      LinkQuoteItems
      CompanionProduct {
        ProductId
        Name
        ProductCode
      }
    }
  }
`;

const PRICE_TYPES = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Hardware', value: 'hardware' },
  { label: 'Setup', value: 'setup' },
];

const RATE_TYPES = [
  { label: 'Local Calls', value: 'LOCAL_CALLS' },
  { label: 'National Calls', value: 'NATIONAL_CALLS' },
  { label: 'Mobile Calls', value: 'MOBILE_CALLS' },
  { label: '1300 Calls', value: '_1300_CALLS' },
  { label: '1800 Calls', value: '_1800_CALLS' },
  { label: 'Mobile Data Overage', value: 'MOBILE_DATA_OVERAGE' },
  { label: 'Data Overage', value: 'DATA_OVERAGE' },
];

const PRODUCT_PRICING_COLUMNS = [
  {
    id: 1,
    field: 'Description',
    label: 'Quote Item',
    description: 'Override the product name for this price row',
  },
  {
    id: 10,
    field: 'ProductCode',
    label: 'Product Code',
    description: 'Override the product SKU for this price row',
  },
  {
    id: 2,
    field: 'PriceType',
    label: 'Price Type',
    description: 'Product Pricing Desc',
  },
  {
    id: 4,
    field: 'Price',
    label: 'Cost Price',
    description: 'Price for Cost type of quote',
  },
  {
    id: 9,
    field: 'Multiplier',
    label: 'Multiplier',
    description: 'Multiplier for CAPEX price quote',
  },
  {
    id: 6,
    field: 'Price',
    label: 'CAPEX Price',
    description: 'Price for CAPEX type of quote',
  },
  {
    id: 5,
    field: 'Price',
    label: 'OPEX Price',
    description: 'Price for OPEX type of quote',
  },
  {
    id: 8,
    field: 'Price',
    label: 'ENERDS Price',
    description: 'Price for ENERDS type of quote',
  },
];

const ProductForm = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const location = useLocation();
  const history = useHistory();

  // initialize state
  const currentQuoteParam =
    location.search && location.search.includes('?currentQuote=')
      ? `?currentQuote=${location.search.split('=').pop()}`
      : '';
  const [formType] = useState(
    location.pathname.match(/\/edit\/[\w\d\W]*/g) ? 'edit' : 'create'
  );
  const [productData, setProductData] = useState({});
  const [quoteTypes, setQuoteTypes] = useState({});
  const [productPrices, setProductPrices] = useState([]);
  const [newPrice, setNewPrice] = useState({
    Description: '',
    ProductCode: '',
    LinkQuoteItems: 1,
    PriceType: PRICE_TYPES[0].value,
    OpexProration: 1,
    ShowInHardwareTable: 1,
    CostPrice: '',
    AllowCostPriceOverride: 0,
    Multiplier: '',
    CapexPrice: '',
    OpexPrice: '',
    EnerdsPrice: '',
  });
  const [productRates, setProductRates] = useState([]);
  const [companionProducts, setCompanionProducts] = useState([]);
  const [init, setInit] = useState(false);
  const [newRate, setNewRate] = useState({
    RateType: RATE_TYPES[0].value,
    OpexRate: '',
    CapexRate: '',
    EnerdsRate: '',
    RateUnits: "",
  });
  const [formResult, setFormResult] = useState(null);
  const [selectedAttributeValues, setSelectedAttributeValues] = useState([]);
  const [selectedCAFTemplates, setSelectedCAFTemplates] = useState([]);
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  // initialize apollo graphql queries and mutations
  const { data, refetch } = useQuery(GET_DATA, {
    variables: { ProductId: props.pathArg ? parseInt(props.pathArg) : 0 },
  });
  const [createProduct, { data: createdProductData }] =
    useMutation(CREATE_PRODUCT);
  const [updateProduct, { data: updatedProductData }] =
    useMutation(UPDATE_PRODUCT);
  const [createProductRates, { data: createdProductRatesData }] =
    useMutation(CREATE_PRODUCT_RATES);
  const [createProductPriceLines, { data: createdProductPriceLinesData }] = useMutation(
    CREATE_PRODUCT_PRICE_LINES
  );
  const [syncProductAttributeValues] = useMutation(SYNC_ATTRIBUTE_VALUES);
  const [syncProductCAFTemplates] = useMutation(SYNC_PRODUCT_CAF_TEMPLATES);
  const [uploadProductImage] = useMutation(UPLOAD_PRODUCT_IMAGE);
  const [syncCompanionProducts] = useMutation(SYNC_COMPANION_PRODUCTS);

  const productDetailsChange = (e, field) => {
    const value = e === null ? null : (e.target ? e.target.value : e.value);
    const product = Object.assign({}, productData);
    if (
      field === 'Active' ||
      field === 'SyncWithXero' ||
      field === 'XeroTrackedAsInventory'
    ) {
      product[field] = e;
   } else {
      if ( field === 'CatId' ) {
        product['CatId'] = value ? parseInt(value) : null;
      }
      else if ( field === 'HasProposalTemplate' ) {
        product['HasProposalTemplate'] = e ? 1 : 0;
      }
      else {
        product[field] = value;
      }
    }
    setProductData(product);
    if (field === 'Name') {
      const rate = Object.assign({}, newRate);
      setNewRate(rate);
    }
  };

  const computePriceFields = (price, field, value) => {
    if (field === 'CapexPrice') {
      price.MultiplierHighlight = !!(price.CostPrice && value);
      price.Multiplier = (price.CostPrice && value) ? parseFloat((value / price.CostPrice).toFixed(4)) : null;
    } else if (field === 'Multiplier') {
      price.CapexPrice = (price.CostPrice && value) ? roundCurrency(price.CostPrice * value) : null;
      price.CapexHighlight = !!(price.CostPrice && value);
    } else {
      price.MultiplierHighlight = false;
      if (price.PriceType === 'hardware') {
        price.CapexPrice = (price.Multiplier && value) ? roundCurrency(value * price.Multiplier) : null;
        price.CapexHighlight = !!(price.Multiplier && value);
      } else {
        price.Multiplier = (price.CapexPrice && value) ? parseFloat((price.CapexPrice / value).toFixed(4)) : null;
        price.CapexHighlight = false;
      }
    }
  };

  const updatePriceEntry = (value, index, field, computations) => {
    const editPrice = Object.assign({}, productPrices[index]);
    const newPrices = Array.from(productPrices);

    editPrice[field] = value;

    editPrice[field + 'Style'] = { width: '100px' };
    if (['CostPrice', 'Multiplier', 'CapexPrice'].includes(field)) {
      computePriceFields(editPrice, field, value);
    }

    newPrices[index] = editPrice;
    setProductPrices(newPrices);
  };

  const onNewPriceChange = (value, field, computations) => {
    const price = Object.assign({}, newPrice);

    price[field] = value;

    price[field + 'Style'] = { width: '100px' };
    if (['CostPrice', 'Multiplier', 'CapexPrice'].includes(field)) {
      computePriceFields(price, field, value);
    }

    setNewPrice(price);
  };

  const onDeletePrice = (index) => {
    const newPrices = Array.from(productPrices);
    newPrices.splice(index, 1);
    setProductPrices(newPrices);
  };

  const addProductPrice = (e) => {
    e.preventDefault();
    const prices = Array.from(productPrices);
    newPrice.PriceLineId =
      productPrices.length > 0
        ? parseInt(productPrices[productPrices.length - 1].PriceLineId) + 1
        : 1;
    data.QuoteTypes.filter((qt) => qt.Name !== 'COST_PRICE').forEach((qt) => {
      const name = capitalizeStr(qt.Name);
      newPrice[`${name}Price`] = newPrice[`${name}Price`] || null;
    });
    prices.push(newPrice);
    setProductPrices(prices);
    setNewPrice({
      Description: '',
      ProductCode: '',
      LinkQuoteItems: 1,
      PriceType: PRICE_TYPES[0].value,
      OpexProration: 1,
      ShowInHardwareTable: 1,
      CostPrice: '',
      AllowCostPriceOverride: 0,
      Multiplier: '',
      CapexPrice: '',
      OpexPrice: '',
      EnerdsPrice: '',
    });
  };

  const transformDBDataToProductPrices = useCallback(() => {
    if (!data?.Product?.ProductPriceLines?.length) return [];
    const sellQuoteTypes = data.QuoteTypes.filter((qt) => qt.Name !== 'COST_PRICE');

    return data.Product.ProductPriceLines.map((line) => {
      const priceByQtId = Object.fromEntries((line.ProductPrices ?? []).map((p) => [p.QuoteTypeId, p]));
      const priceFields = {};
      sellQuoteTypes.forEach((qt) => {
        const p = priceByQtId[parseInt(qt.QuoteTypeId)];
        const name = capitalizeStr(qt.Name);
        priceFields[`${name}Price`] = p?.Price ?? null;
        priceFields[`ShowPrice${name}`] = p?.ShowPrice ?? 1;
        priceFields[`DisplayProduct${name}`] = p?.DisplayProduct ?? 1;
        priceFields[`IncludePriceInTotal${name}`] = p?.IncludePriceInTotal ?? 1;
      });
      return {
        PriceLineId: line.PriceLineId,
        Description: line.Description ?? '',
        ProductCode: line.ProductCode ?? '',
        LinkQuoteItems: line.LinkQuoteItems ?? 1,
        PriceType: line.PriceType ?? '',
        OpexProration: line.OpexProration ?? 0,
        ShowInHardwareTable: line.ShowInHardwareTable ?? 0,
        CostPrice: line.CostPrice,
        AllowCostPriceOverride: line.AllowCostPriceOverride ?? 0,
        Multiplier: line.Multiplier,
        ...priceFields,
      };
    });
  }, [data]);

  const transformToProductPricesDBData = () => {
    const sellQuoteTypes = data.QuoteTypes.filter((qt) => qt.Name !== 'COST_PRICE');
    return productPrices.map((price) => ({
      PriceLineId: price.PriceLineId ? parseInt(price.PriceLineId) : null,
      PriceType: price.PriceType,
      Description: price.Description || null,
      ProductCode: price.ProductCode || null,
      CostPrice: price.CostPrice !== '' && price.CostPrice != null ? parseFloat(price.CostPrice) : null,
      Multiplier: price.Multiplier !== '' && price.Multiplier != null ? parseFloat(price.Multiplier) : null,
      AllowCostPriceOverride: price.AllowCostPriceOverride ?? 0,
      OpexProration: price.OpexProration ?? 1,
      LinkQuoteItems: price.LinkQuoteItems ?? 1,
      ShowInHardwareTable: price.ShowInHardwareTable ?? 1,
      Prices: sellQuoteTypes.map((qt) => {
        const name = capitalizeStr(qt.Name);
        const rawPrice = price[`${name}Price`];
        return {
          QuoteTypeId: parseInt(qt.QuoteTypeId),
          Price: rawPrice !== '' && rawPrice != null ? parseFloat(rawPrice) : null,
          ShowPrice: price[`ShowPrice${name}`] ?? 1,
          DisplayProduct: price[`DisplayProduct${name}`] ?? 1,
          IncludePriceInTotal: price[`IncludePriceInTotal${name}`] ?? 1,
        };
      }),
    }));
  };

  // PRODUCT RATES

  const updateRateEntry = (value, index, field) => {
    const editRate = Object.assign({}, productRates[index]);
    editRate[field] = value;

    const newRates = Array.from(productRates);
    newRates[index] = editRate;
    setProductRates(newRates);
  };

  const onNewRateChange = (value, field) => {
    const rate = Object.assign({}, newRate);
    rate[field] = value;
    setNewRate(rate);
  };

  const onDeleteRate = (index) => {
    const newRates = Array.from(productRates);
    newRates.splice(index, 1);
    setProductRates(newRates);
  };

  const addProductRate = (e) => {
    e.preventDefault();
    const rate = Array.from(productRates);
    newRate.RateLineId =
      productRates.length > 0 ? parseInt(productRates.pop().RateLineId) + 1 : 1;
    rate.push(newRate);
    setProductRates(rate);
    setNewRate({
      RateType: RATE_TYPES[0].value,
      OpexRate: '',
      CapexRate: '',
      EnerdsRate: '',
      RateUnits: "",
    });
  };

  const transformDBDataToProductRates = useCallback(() => {
    let transformedProductRates = [];
    if (data && data.Product && data.Product.ProductRates.length > 0) {
      const rates = data.Product.ProductRates;
      const findTypeId = (name) => {
        const match = data.QuoteTypes.filter((quoteType) => quoteType.Name === name)[0];
        return match ? parseInt(match.QuoteTypeId) : null;
      };
      const opexId = findTypeId('OPEX');
      const capexId = findTypeId('CAPEX');
      const enerdsId = findTypeId('ENERDS');

      const rateLines = [
        ...new Set(rates.map((rate) => parseInt(rate.RateLineId))),
      ];
      rateLines.forEach((line) => {
        transformedProductRates.push({
          ProductId: data.Product.ProductId,
          RateLineId: line,
          RateType: rates.filter(
            (rate) => parseInt(rate.RateLineId) === line
          )[0].RateType,
          OpexRate: opexId !== null ? (rates.filter((rate) => parseInt(rate.RateLineId) === line && opexId === parseInt(rate.QuoteTypeId))[0] || {}).Rate : null,
          CapexRate: capexId !== null ? (rates.filter((rate) => parseInt(rate.RateLineId) === line && capexId === parseInt(rate.QuoteTypeId))[0] || {}).Rate : null,
          EnerdsRate: enerdsId !== null ? (rates.filter((rate) => parseInt(rate.RateLineId) === line && enerdsId === parseInt(rate.QuoteTypeId))[0] || {}).Rate : null,
          RateUnits: rates.filter(
            (rate) => parseInt(rate.RateLineId) === line
          )[0].RateUnits,
        });
      });
    }
    return transformedProductRates;
  }, [data]);

  const transformToProductRatesDBData = () => {
    const quoteTypes = Array.from(data.QuoteTypes).slice(1);
    let transformedProductRates = [];
    productRates.forEach((rate) => {
      quoteTypes.forEach((quoteType) => {
        const rawRate = rate[`${capitalizeStr(quoteType.Name)}Rate`];
        transformedProductRates.push({
          RateLineId: parseInt(rate.RateLineId),
          ProductId:
            data.Product && data.Product.ProductId
              ? data.Product.ProductId
              : productData.ProductId,
          RateType: rate.RateType,
          RateUnits: rate.RateUnits,
          Rate: rawRate !== '' ? parseFloat(rawRate) : null,
          QuoteTypeId: parseInt(quoteType.QuoteTypeId),
        });
      });
    });
    return transformedProductRates;
  };

  const saveProductRelations = (productId, imageFile = null) => {
    if (imageFile) {
      uploadProductImage({ variables: { productId, file: imageFile } }).then(({ data: uploadData }) => {
        const filename = uploadData.uploadProductImage;
        setProductImagePreview(getProductImageUrl(filename));
        updateProduct({ variables: { ProductInput: { ProductId: productId, Image: filename } } });
      });
    }
    createProductPriceLines({
      variables: {
        ProductPriceLineInput: transformToProductPricesDBData(),
        ProductId: productId,
      },
    });
    createProductRates({
      variables: {
        ProductRatesList: transformToProductRatesDBData(),
        ProductId: productId,
      },
    });
    syncProductAttributeValues({
      variables: {
        ProductId: productId,
        AttributeValueIds: selectedAttributeValues.map(v => v.value),
      },
    });
    syncProductCAFTemplates({
      variables: {
        ProductId: productId,
        CAFTemplateIds: selectedCAFTemplates.map(t => t.value),
      },
    });
    syncCompanionProducts({
      variables: {
        ProductId: productId,
        input: companionProducts.map(c => ({
          CompanionId: c.CompanionId,
          Quantity: c.Quantity,
          LinkQuoteItems: c.LinkQuoteItems ? 1 : 0,
        })),
      },
    });
  };

  const saveProduct = () => {
    if (!productData.Name || !productData.ProductCode) {
      setFormResult({ success: false, message: 'Product Code and Name are required.' });
      return;
    }
    if (productData.SyncWithXero && (!productData.XeroCOA || !productData.XeroCOASales)) {
      setFormResult({ success: false, message: 'Chart of Accounts (Purchase and Sell) are required when Xero sync is enabled.' });
      return;
    }
    if (productData.XeroTrackedAsInventory && !productData.XeroInventoryAssetAccountCode) {
      setFormResult({ success: false, message: 'Inventory Asset Account Code is required when inventory tracking is enabled.' });
      return;
    }
    if (formType === 'create') {
      createProduct({
        variables: { ProductInput: productData },
      }).then(({ data: result }) => {
        saveProductRelations(result.createProduct.ProductId, pendingImageFile);
        setFormResult({ success: true, message: 'Successfully created a new product' });
      });
    } else {
      updateProduct({
        variables: { ProductInput: productData },
      });
      saveProductRelations(productData.ProductId);
    }
  };

  useEffect(() => {
    if (
      updatedProductData &&
      updatedProductData.updateProduct &&
      formResult === null &&
      (createdProductRatesData || createdProductPriceLinesData)
    ) {
      setFormResult({
        success: true,
        message: 'Successfully updated product',
      });
    }
  }, [createdProductData, updatedProductData, formResult, createdProductRatesData, createdProductPriceLinesData]);

  useEffect(() => {
    if (data && data.QuoteTypes) {
      const newQuoteTypes = {};
      data.QuoteTypes.forEach((price_type) => {
        newQuoteTypes[price_type.Name] = price_type;
      });
      setQuoteTypes(newQuoteTypes);
    }
  }, [data]);

  useEffect(() => {
    if (data && data.Product && init === false) {
      // initialize product details and prices/rates
      setProductData({
        ProductId: data.Product.ProductId,
        ProductCode: data.Product.ProductCode,
        Name: data.Product.Name,
        CatId: parseInt(data.Product.CatId),
        ProvisioningTypeId: data.Product.ProvisioningTypeId,
        SyncWithXero: data.Product.SyncWithXero,
        XeroCOA: data.Product.XeroCOA,
        XeroCOASales: data.Product.XeroCOASales,
        XeroInventoryAssetAccountCode:
          data.Product.XeroInventoryAssetAccountCode,
        XeroTrackedAsInventory: data.Product.XeroTrackedAsInventory,
        Active: data.Product.Active,
        HasProposalTemplate: data.Product.HasProposalTemplate ? 1 : 0,
        ProposalContent: data.Product.ProposalContent,
        ProposalLink: data.Product.ProposalLink,
        Image: data.Product.Image,
      });

      const productRates = transformDBDataToProductRates();
      setProductRates(productRates);
      const productPrices = transformDBDataToProductPrices();
      setProductPrices(productPrices);

      setSelectedAttributeValues(
        (data.Product.AttributeValues || []).map(v => ({
          value: v.AttributeValueId,
          label: v.Value,
          attrName: v.Attribute.Name,
          attributeId: v.AttributeId,
        }))
      );

      setSelectedCAFTemplates(
        (data.Product.CAFTemplates || []).map(t => ({
          value: parseInt(t.CAFTemplateId),
          label: t.Name,
        }))
      );

      setCompanionProducts(
        (data.Product.Companions || []).map(c => ({
          CompanionProductId: c.CompanionProductId,
          CompanionId: c.CompanionId,
          Name: c.CompanionProduct?.Name,
          ProductCode: c.CompanionProduct?.ProductCode,
          Quantity: c.Quantity,
          LinkQuoteItems: c.LinkQuoteItems,
        }))
      );

      setInit(true);
    } else if (formType.toLowerCase() === 'create' && init === false) {
      setProductData({ Active: 1 });
      setInit(true);
    }
  }, [data, init, formType, newPrice, transformDBDataToProductPrices, transformDBDataToProductRates]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <React.Fragment>
      {formResult && formResult.success && (
        <SweetAlert
          success
          title="Success"
          onConfirm={(e) =>
            history.replace(`/admin/products${currentQuoteParam}`)
          }
          showConfirm={false}
          timeout={2500}
        >
          {formResult.message}
        </SweetAlert>
      )}
      {formResult && formResult.success === false && (
        <SweetAlert
          error
          title="Error"
          onConfirm={(e) => setFormResult(null)}
          showConfirm={true}
        >
          {formResult.message}
        </SweetAlert>
      )}
      {data &&
        ((data.Product === null && formType === 'create') ||
          (data.Product && formType === 'edit')) && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="12">
                    {data && data.Product ? (
                      <h4 className="page-title">{`Edit Product - ${data.Product.Name}`}</h4>
                    ) : (
                      <h4 className="page-title">{`Create Product`}</h4>
                    )}
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardHeader>Product Details</CardHeader>
                    <CardBody>
                      <Form>
                        <FormGroup row>
                          <Label sm={4}>Product Code <small className="text-danger">*</small></Label>
                          <Col sm={8}>
                            <Input
                              type="text"
                              value={productData.ProductCode || ''}
                              onChange={(e) =>
                                productDetailsChange(e, 'ProductCode')
                              }
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Label sm={4}>Product Name <small className="text-danger">*</small></Label>
                          <Col sm={8}>
                            <Input
                              type="text"
                              defaultValue={
                                data.Product && data.Product.Name
                                  ? data.Product.Name
                                  : ''
                              }
                              onChange={(e) => productDetailsChange(e, 'Name')}
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Label sm={4}>Category</Label>
                          <Col sm={8}>
                            <Select
                              isClearable
                              placeholder={'Select category...'}
                              options={data.ProductCategories.map((cat) => ({
                                value: cat.CatId,
                                label: cat.Name,
                              }))}
                              menuPortalTarget={document.querySelector('body')}
                              onChange={(e) => productDetailsChange(e, 'CatId')}
                              defaultValue={
                                data.Product && data.Product.ProductCategory
                                  ? {
                                    value: data.Product.ProductCategory.CatId,
                                    label: data.Product.ProductCategory.Name,
                                  }
                                  : null
                              }
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Label sm={4}>Attributes</Label>
                          <Col sm={8}>
                            <Select
                              isMulti
                              options={(() => {
                                const usedAttrIds = new Set(selectedAttributeValues.map(v => v.attributeId));
                                return (data?.Attributes ?? [])
                                  .map(attr => ({
                                    label: attr.Name,
                                    options: attr.Values
                                      .filter(() => !usedAttrIds.has(attr.AttributeId))
                                      .map(v => ({
                                        value: v.AttributeValueId,
                                        label: v.Value,
                                        attrName: attr.Name,
                                        attributeId: attr.AttributeId,
                                      })),
                                  }))
                                  .filter(group => group.options.length > 0);
                              })()}
                              value={selectedAttributeValues}
                              onChange={(selected) => setSelectedAttributeValues(selected || [])}
                              formatOptionLabel={(opt, { context }) =>
                                context === 'value' ? `${opt.attrName}: ${opt.label}` : opt.label
                              }
                              placeholder="Add attributes..."
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Label sm={4}>Required CAF Templates</Label>
                          <Col sm={8}>
                            <Select
                              isMulti
                              placeholder="Select CAF templates..."
                              options={(data.CAFTemplates || [])
                                .filter(t => t.CAFSectionId)
                                .map(t => ({
                                  value: parseInt(t.CAFTemplateId),
                                  label: t.Name,
                                }))}
                              value={selectedCAFTemplates}
                              onChange={setSelectedCAFTemplates}
                              menuPortalTarget={document.querySelector('body')}
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Label sm={4}>Provisioning Type</Label>
                          <Col sm={8}>
                            <Select
                              isClearable
                              options={(data?.ProvisioningTypes ?? []).map(pt => ({
                                value: pt.ProvisioningTypeId,
                                label: pt.Name,
                              }))}
                              value={(data?.ProvisioningTypes ?? [])
                                .filter(pt => pt.ProvisioningTypeId === productData.ProvisioningTypeId)
                                .map(pt => ({ value: pt.ProvisioningTypeId, label: pt.Name }))[0] || null
                              }
                              onChange={(selected) => setProductData(prev => ({
                                ...prev,
                                ProvisioningTypeId: selected ? selected.value : null,
                              }))}
                            />
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Col sm={{ size: 8, offset: 4 }} className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              id="Active"
                              checked={!!(productData && productData.Active)}
                              onChange={(e) =>
                                productDetailsChange(
                                  e.target.checked ? 1 : 0,
                                  'Active'
                                )
                              }
                            />
                            <Label for="Active" className="ml-2 mb-0">Active</Label>
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Col sm={{ size: 8, offset: 4 }} className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              id="SyncWithXero"
                              checked={!!(productData && productData.SyncWithXero)}
                              onChange={(e) =>
                                productDetailsChange(
                                  e.target.checked ? 1 : 0,
                                  'SyncWithXero'
                                )
                              }
                            />
                            <Label for="SyncWithXero" className="ml-2 mb-0">Sync with Xero</Label>
                          </Col>
                        </FormGroup>
                        {!!productData.SyncWithXero && (
                          <>
                            <FormGroup row>
                              <Label sm={4}>Chart of Accounts (Purchase) <span className="text-danger">*</span></Label>
                              <Col sm={8}>
                                <Select
                                  placeholder={'Select account for purchases...'}
                                  isClearable
                                  options={data.XeroCOA.map((coa) => ({
                                    value: coa.XeroCOAId,
                                    label: coa.Account,
                                  }))}
                                  menuPortalTarget={document.querySelector('body')}
                                  onChange={(e) =>
                                    productDetailsChange(e, 'XeroCOA')
                                  }
                                  defaultValue={
                                    data.Product && data.Product.XeroCOA && data.Product.XeroAccount
                                      ? {
                                        value: data.Product.XeroCOA,
                                        label: data.Product.XeroAccount.Account,
                                      }
                                      : null
                                  }
                                />
                              </Col>
                            </FormGroup>
                            <FormGroup row>
                              <Label sm={4}>Chart of Accounts (Sell) <span className="text-danger">*</span></Label>
                              <Col sm={8}>
                                <Select
                                  placeholder={'Select account for sales...'}
                                  isClearable
                                  options={data.XeroCOA.map((coa) => ({
                                    value: coa.XeroCOAId,
                                    label: coa.Account,
                                  }))}
                                  menuPortalTarget={document.querySelector('body')}
                                  onChange={(e) =>
                                    productDetailsChange(e, 'XeroCOASales')
                                  }
                                  defaultValue={
                                    data.Product && data.Product.XeroCOASales && data.Product.XeroAccountSales
                                      ? {
                                        value: data.Product.XeroCOASales,
                                        label: data.Product.XeroAccountSales.Account,
                                      }
                                      : null
                                  }
                                />
                              </Col>
                            </FormGroup>
                            <FormGroup row>
                              <Col sm={{ size: 8, offset: 4 }} className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  id="XeroTrackedAsInventory"
                                  checked={!!(productData && productData.XeroTrackedAsInventory)}
                                  onChange={(e) =>
                                    productDetailsChange(
                                      e.target.checked ? 1 : 0,
                                      'XeroTrackedAsInventory'
                                    )
                                  }
                                />
                                <Label for="XeroTrackedAsInventory" className="ml-2 mb-0">Inventory tracked in Xero</Label>
                              </Col>
                            </FormGroup>
                            {!!productData.XeroTrackedAsInventory && (
                              <FormGroup row>
                                <Label sm={4}>Inventory Asset Account Code <span className="text-danger">*</span></Label>
                                <Col sm={8}>
                                  <Input
                                    type="number"
                                    defaultValue={
                                      data.Product &&
                                        data.Product.XeroInventoryAssetAccountCode
                                        ? data.Product.XeroInventoryAssetAccountCode
                                        : ''
                                    }
                                    onChange={(e) =>
                                      productDetailsChange(
                                        e,
                                        'XeroInventoryAssetAccountCode'
                                      )
                                    }
                                  />
                                </Col>
                              </FormGroup>
                            )}
                          </>
                        )}
                        <FormGroup row>
                          <Col sm={{ size: 8, offset: 4 }} className="d-flex align-items-center">
                            <input
                              type="checkbox"
                              id="HasProposalTemplate"
                              checked={
                                !!(productData && productData.HasProposalTemplate)
                              }
                              onChange={(e) =>
                                productDetailsChange(
                                  e.target.checked ? 1 : 0,
                                  'HasProposalTemplate'
                                )
                              }
                            />
                            <Label for="HasProposalTemplate" className="ml-2 mb-0">Include product details on proposals</Label>
                          </Col>
                        </FormGroup>

                        {productData && !!productData.HasProposalTemplate && (
                          <>
                            <FormGroup row>
                              <Label sm={4}>Product Image</Label>
                              <Col sm={8}>
                                <ImageDropzone
                                  imageUrl={
                                    productImagePreview ??
                                    (productData.Image
                                      ? getProductImageUrl(productData.Image)
                                      : null)
                                  }
                                  name="product image"
                                  onDrop={([file]) => {
                                    if (!data.Product?.ProductId) {
                                      setPendingImageFile(file);
                                      setProductImagePreview(URL.createObjectURL(file));
                                      return;
                                    }
                                    uploadProductImage({
                                      variables: {
                                        productId: data.Product.ProductId,
                                        file,
                                      },
                                    }).then(({ data: uploadData }) => {
                                      const filename = uploadData.uploadProductImage;
                                      setProductImagePreview(getProductImageUrl(filename));
                                      setProductData((prev) => ({
                                        ...prev,
                                        Image: filename,
                                      }));
                                    });
                                  }}
                                />
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm={4}>Product Description</Label>
                              <Col sm={8}>
                                <Input
                                  type="textarea"
                                  rows={6}
                                  value={productData.ProposalContent || ''}
                                  onChange={(e) =>
                                    productDetailsChange(e, 'ProposalContent')
                                  }
                                />
                              </Col>
                            </FormGroup>

                            <FormGroup row>
                              <Label sm={4}>Learn More URL</Label>
                              <Col sm={8}>
                                <Input
                                  type="text"
                                  value={productData.ProposalLink || ''}
                                  onChange={(e) =>
                                    productDetailsChange(e, 'ProposalLink')
                                  }
                                />
                              </Col>
                            </FormGroup>
                          </>
                        )}
                      </Form>
                    </CardBody>
                  </Card>

                  <h6>Product Pricing</h6>
                  <Card>
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <table className="table baseline">
                          <thead className="thead-dark">
                            <tr>
                              {PRODUCT_PRICING_COLUMNS.map((column) => (
                                <ColumnHeadersTooltip
                                  key={column.id}
                                  id={'product-pricing'}
                                  column={column}
                                />
                              ))}
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {productPrices.length > 0 ? (
                              productPrices.map((price, index) => (
                                <tr key={index}>
                                  <td>
                                    <Input
                                      type="text"
                                      placeholder={productData.Name || ''}
                                      value={
                                        price.Description
                                          ? price.Description
                                          : ''
                                      }
                                      onChange={(e) =>
                                        updatePriceEntry(
                                          e.target.value,
                                          index,
                                          'Description'
                                        )
                                      }
                                    />
                                    <div className="display-checkboxes">
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          checked={!!price.LinkQuoteItems}
                                          onChange={(e) => updatePriceEntry(e.target.checked ? 1 : 0, index, 'LinkQuoteItems')}
                                        />
                                        <span className="display-checkbox-text">Link quote items</span>
                                      </label>
                                    </div>
                                  </td>
                                  <td>
                                    <Input
                                      type="text"
                                      placeholder={productData.ProductCode || ''}
                                      value={price.ProductCode || ''}
                                      onChange={(e) =>
                                        updatePriceEntry(
                                          e.target.value,
                                          index,
                                          'ProductCode'
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Select
                                      options={PRICE_TYPES.map(
                                        (priceType) => ({
                                          value: priceType.value,
                                          label: priceType.label,
                                        })
                                      )}
                                      menuPortalTarget={document.querySelector(
                                        'body'
                                      )}
                                      value={
                                        PRICE_TYPES.filter(
                                          (priceType) =>
                                            priceType.value ===
                                            price.PriceType
                                        ).map((priceType) => ({
                                          value: priceType.value,
                                          label: priceType.label,
                                        }))[0]
                                      }
                                      onChange={(e) =>
                                        updatePriceEntry(
                                          e.value,
                                          index,
                                          'PriceType'
                                        )
                                      }
                                    />
                                    {price.PriceType === 'hardware' && (
                                      <div className="display-checkboxes">
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            checked={!!price.ShowInHardwareTable}
                                            onChange={(e) =>
                                              updatePriceEntry(e.target.checked ? 1 : 0, index, 'ShowInHardwareTable')
                                            }
                                          />
                                          <span className="display-checkbox-text">List as hardware item</span>
                                        </label>
                                        <label className="display-checkbox-wrapper">
                                          <input
                                            type="checkbox"
                                            checked={!!price.OpexProration}
                                            onChange={(e) =>
                                              updatePriceEntry(e.target.checked ? 1 : 0, index, 'OpexProration')
                                            }
                                          />
                                          <span className="display-checkbox-text">OPEX proration</span>
                                        </label>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      className="price-input"
                                      value={price.CostPrice ?? ''}
                                      onChange={(e) =>
                                        updatePriceEntry(
                                          e.target.value,
                                          index,
                                          'CostPrice'
                                        )
                                      }
                                    />
                                    <div className="display-checkboxes">
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          checked={!!price.AllowCostPriceOverride}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'AllowCostPriceOverride'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Allow override</span>
                                      </label>
                                    </div>
                                  </td>
                                  <td>
                                    {price.PriceType === 'hardware' && (
                                      <Input
                                          type="number"
                                          className={`price-input${price.MultiplierHighlight ? ' price-input-highlight' : ''}`}
                                          placeholder={quoteTypes.CAPEX.DefaultMultiplier}
                                          value={price.Multiplier ?? ''}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.value,
                                              index,
                                              'Multiplier'
                                            )
                                          }
                                        />
                                    )}
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      className={`price-input${price.CapexHighlight ? ' price-input-highlight' : ''}`}
                                      value={price.CapexPrice ?? ''}
                                      onChange={(e) =>
                                        updatePriceEntry(
                                          e.target.value,
                                          index,
                                          'CapexPrice'
                                        )
                                      }
                                    />
                                    <div className="display-checkboxes">
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.DisplayProductCapex ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'DisplayProductCapex'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Display product</span>
                                      </label>
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.ShowPriceCapex ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'ShowPriceCapex'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Display price</span>
                                      </label>
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.IncludePriceInTotalCapex ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'IncludePriceInTotalCapex'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Include in total</span>
                                      </label>
                                    </div>
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      className="price-input"
                                      value={price.OpexPrice ?? ''}
                                      readOnly={!!(price.PriceType === 'hardware' && price.OpexProration)}
                                      onChange={(e) =>
                                        updatePriceEntry(
                                          e.target.value,
                                          index,
                                          'OpexPrice'
                                        )
                                      }
                                    />
                                    <div className="display-checkboxes">
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.DisplayProductOpex ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'DisplayProductOpex'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Display product</span>
                                      </label>
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.ShowPriceOpex ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'ShowPriceOpex'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Display price</span>
                                      </label>
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.IncludePriceInTotalOpex ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'IncludePriceInTotalOpex'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Include in total</span>
                                      </label>
                                    </div>
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      className="price-input"
                                      value={price.EnerdsPrice ?? ''}
                                      readOnly={!!(price.PriceType === 'hardware' && price.OpexProration)}
                                      onChange={(e) =>
                                        updatePriceEntry(
                                          e.target.value,
                                          index,
                                          'EnerdsPrice'
                                        )
                                      }
                                    />
                                    <div className="display-checkboxes">
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.DisplayProductEnerds ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'DisplayProductEnerds'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Display product</span>
                                      </label>
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.ShowPriceEnerds ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'ShowPriceEnerds'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Display price</span>
                                      </label>
                                      <label className="display-checkbox-wrapper">
                                        <input
                                          type="checkbox"
                                          defaultChecked={!!(price.IncludePriceInTotalEnerds ?? 1)}
                                          onChange={(e) =>
                                            updatePriceEntry(
                                              e.target.checked ? 1 : 0,
                                              index,
                                              'IncludePriceInTotalEnerds'
                                            )
                                          }
                                        />
                                        <span className="display-checkbox-text">Include in total</span>
                                      </label>
                                    </div>
                                  </td>
                                  <td>
                                    <Button
                                      color="danger"
                                      onClick={(e) => onDeletePrice(index)}
                                    >
                                      Delete
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr className="text-center">
                                <td colSpan={9}>No Product Prices</td>
                              </tr>
                            )}
                          </tbody>
                          <tbody>
                            <tr>
                              <th style={{ minWidth: '320px' }}>Quote Item</th>
                              <th style={{ minWidth: '140px' }}>Product Code</th>
                              <th style={{ minWidth: '140px' }}>Price Type</th>
                              <th style={{ minWidth: '140px' }}>Cost Price</th>
                              <th style={{ minWidth: '100px' }}>Multiplier</th>
                              <th style={{ minWidth: '140px' }}>CAPEX Price</th>
                              <th style={{ minWidth: '140px' }}>OPEX Price</th>
                              <th style={{ minWidth: '140px' }}>ENERDS Price</th>
                              <th></th>
                            </tr>

                            <tr>
                              <td>
                                <Input
                                  type="text"
                                  placeholder={productData.Name || ''}
                                  value={newPrice.Description}
                                  onChange={(e) =>
                                    onNewPriceChange(
                                      e.target.value,
                                      'Description'
                                    )
                                  }
                                />
                                <div className="display-checkboxes">
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!newPrice.LinkQuoteItems}
                                      onChange={(e) => onNewPriceChange(e.target.checked ? 1 : 0, 'LinkQuoteItems')}
                                    />
                                    <span className="display-checkbox-text">Link quote items</span>
                                  </label>
                                </div>
                              </td>
                              <td>
                                <Input
                                  type="text"
                                  placeholder={productData.ProductCode || ''}
                                  value={newPrice.ProductCode}
                                  onChange={(e) =>
                                    onNewPriceChange(
                                      e.target.value,
                                      'ProductCode'
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <Select
                                  options={PRICE_TYPES.map((priceType) => ({
                                    value: priceType.value,
                                    label: priceType.label,
                                  }))}
                                  value={
                                    PRICE_TYPES.filter(
                                      (priceType) =>
                                        priceType.value === newPrice.PriceType
                                    ).map((priceType) => ({
                                      value: priceType.value,
                                      label: priceType.label,
                                    }))[0]
                                  }
                                  menuPortalTarget={document.querySelector(
                                    'body'
                                  )}
                                  defaultValue={null}
                                  onChange={(e) =>
                                    onNewPriceChange(e.value, 'PriceType')
                                  }
                                />
                                {newPrice.PriceType === 'hardware' && (
                                  <div className="display-checkboxes">
                                    <label className="display-checkbox-wrapper">
                                      <input
                                        type="checkbox"
                                        checked={!!newPrice.ShowInHardwareTable}
                                        onChange={(e) =>
                                          onNewPriceChange(e.target.checked ? 1 : 0, 'ShowInHardwareTable')
                                        }
                                      />
                                      <span className="display-checkbox-text">List as hardware item</span>
                                    </label>
                                    <label className="display-checkbox-wrapper">
                                      <input
                                        type="checkbox"
                                        checked={!!newPrice.OpexProration}
                                        onChange={(e) =>
                                          onNewPriceChange(e.target.checked ? 1 : 0, 'OpexProration')
                                        }
                                      />
                                      <span className="display-checkbox-text">OPEX proration</span>
                                    </label>
                                  </div>
                                )}
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  className="price-input"
                                  value={newPrice.CostPrice}
                                  onChange={(e) =>
                                    onNewPriceChange(
                                      e.target.value,
                                      'CostPrice'
                                    )
                                  }
                                />
                                <div className="display-checkboxes">
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!newPrice.AllowCostPriceOverride}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'AllowCostPriceOverride'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Allow override</span>
                                  </label>
                                </div>
                              </td>
                              <td>
                                {newPrice.PriceType === 'hardware' && (
                                  <Input
                                    type="number"
                                    className={`price-input${newPrice.MultiplierHighlight ? ' price-input-highlight' : ''}`}
                                    placeholder={quoteTypes.CAPEX.DefaultMultiplier}
                                    value={newPrice.Multiplier ?? ''}
                                    onChange={(e) =>
                                      onNewPriceChange(
                                        e.target.value,
                                        'Multiplier'
                                      )
                                    }
                                  />
                                )}
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  className={`price-input${newPrice.CapexHighlight ? ' price-input-highlight' : ''}`}
                                  value={newPrice.CapexPrice ?? ''}
                                  onChange={(e) =>
                                    onNewPriceChange(
                                      e.target.value,
                                      'CapexPrice'
                                    )
                                  }
                                />
                                <div className="display-checkboxes">
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.DisplayProductCapex ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'DisplayProductCapex'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Display product</span>
                                  </label>
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.ShowPriceCapex ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'ShowPriceCapex'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Display price</span>
                                  </label>
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.IncludePriceInTotalCapex ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'IncludePriceInTotalCapex'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Include in total</span>
                                  </label>
                                </div>
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  className="price-input"
                                  value={newPrice.OpexPrice}
                                  readOnly={!!(newPrice.PriceType === 'hardware' && newPrice.OpexProration)}
                                  onChange={(e) =>
                                    onNewPriceChange(
                                      e.target.value,
                                      'OpexPrice'
                                    )
                                  }
                                />
                                <div className="display-checkboxes">
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.DisplayProductOpex ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'DisplayProductOpex'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Display product</span>
                                  </label>
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.ShowPriceOpex ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'ShowPriceOpex'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Display price</span>
                                  </label>
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.IncludePriceInTotalOpex ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'IncludePriceInTotalOpex'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Include in total</span>
                                  </label>
                                </div>
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  className="price-input"
                                  value={newPrice.EnerdsPrice}
                                  readOnly={!!(newPrice.PriceType === 'hardware' && newPrice.OpexProration)}
                                  onChange={(e) =>
                                    onNewPriceChange(
                                      e.target.value,
                                      'EnerdsPrice'
                                    )
                                  }
                                />
                                <div className="display-checkboxes">
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.DisplayProductEnerds ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'DisplayProductEnerds'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Display product</span>
                                  </label>
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.ShowPriceEnerds ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'ShowPriceEnerds'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Display price</span>
                                  </label>
                                  <label className="display-checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      checked={!!(newPrice.IncludePriceInTotalEnerds ?? 1)}
                                      onChange={(e) =>
                                        onNewPriceChange(
                                          e.target.checked ? 1 : 0,
                                          'IncludePriceInTotalEnerds'
                                        )
                                      }
                                    />
                                    <span className="display-checkbox-text">Include in total</span>
                                  </label>
                                </div>
                              </td>
                              <td>
                                <Button
                                  color="success"
                                  block
                                  onClick={addProductPrice}
                                >
                                  Add
                                </Button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>

                  {/* COMPANION PRODUCTS */}

                  <h6>Companion Products</h6>
                  <Card>
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="thead-dark">
                            <tr>
                              <th>Product Code</th>
                              <th>Product Name</th>
                              <th>Quantity</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {companionProducts.length > 0 ? companionProducts.map((companion, index) => (
                              <tr key={index}>
                                <td>{companion.ProductCode}</td>
                                <td>{companion.Name}</td>
                                <td>
                                  <Input
                                    type="number"
                                    style={{ width: '100px' }}
                                    value={companion.Quantity ?? ''}
                                    disabled={!!companion.LinkQuoteItems}
                                    onChange={(e) => {
                                      const updated = [...companionProducts];
                                      updated[index] = { ...updated[index], Quantity: e.target.value === '' ? null : parseInt(e.target.value, 10) };
                                      setCompanionProducts(updated);
                                    }}
                                  />
                                  <div className="display-checkboxes">
                                    <label className="display-checkbox-wrapper">
                                      <input
                                        type="checkbox"
                                        checked={!!companion.LinkQuoteItems}
                                        onChange={(e) => {
                                          const updated = [...companionProducts];
                                          updated[index] = { ...updated[index], LinkQuoteItems: e.target.checked ? 1 : 0 };
                                          if (e.target.checked) updated[index].Quantity = 1;
                                          setCompanionProducts(updated);
                                        }}
                                      />
                                      <span className="display-checkbox-text">Link quote items</span>
                                    </label>
                                  </div>
                                </td>
                                <td align="right">
                                  <Button color="danger" onClick={() => setCompanionProducts(companionProducts.filter((_, i) => i !== index))}>Delete</Button>
                                </td>
                              </tr>
                            )) : (
                              <tr className="text-center">
                                <td colSpan={4}>No Companion Products</td>
                              </tr>
                            )}
                            <tr>
                              <td colSpan={4}>
                                <Select
                                  options={(data?.Products || [])
                                    .filter(p => p.ProductId !== productData.ProductId && !companionProducts.find(c => c.CompanionId === p.ProductId))
                                    .map(p => ({ value: p.ProductId, label: p.Name, ProductCode: p.ProductCode }))}
                                  placeholder="Add companion product..."
                                  onChange={(opt) => {
                                    if (!opt) return;
                                    const product = data.Products.find(p => p.ProductId === opt.value);
                                    setCompanionProducts([...companionProducts, {
                                      CompanionId: product.ProductId,
                                      Name: product.Name,
                                      ProductCode: product.ProductCode,
                                      Quantity: 1,
                                      LinkQuoteItems: 1,
                                    }]);
                                  }}
                                  value={null}
                                  filterOption={(option, inputValue) =>
                                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                    option.data.ProductCode?.toLowerCase().includes(inputValue.toLowerCase())
                                  }
                                  menuPortalTarget={document.body}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>

                  {/* PRODUCT RATES */}

                  <h6>Usage Rates</h6>
                  <Card>
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="thead-dark">
                            <tr>
                              <th width="30%">Rate Type</th>
                              <th>CAPEX Rate</th>
                              <th>OPEX Rate</th>
                              <th>ENERDS Rate</th>
                              <th>Rate Units</th>
                              <th width="1"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {productRates.length > 0 ? (
                              productRates.map((rate, index) => (
                                <tr key={index}>
                                  <td>
                                    <Select
                                      options={RATE_TYPES.map((rateType) => ({
                                        value: rateType.value,
                                        label: rateType.label,
                                      }))}
                                      menuPortalTarget={document.querySelector(
                                        'body'
                                      )}
                                      value={
                                        RATE_TYPES.filter(
                                          (rateType) =>
                                            rateType.value === rate.RateType
                                        ).map((rateType) => ({
                                          value: rateType.value,
                                          label: rateType.label,
                                        }))[0]
                                      }
                                      onChange={(e) =>
                                        updateRateEntry(
                                          e.value,
                                          index,
                                          'RateType'
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      value={rate.CapexRate}
                                      onChange={(e) =>
                                        updateRateEntry(
                                          e.target.value,
                                          index,
                                          'CapexRate'
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      value={rate.OpexRate}
                                      onChange={(e) =>
                                        updateRateEntry(
                                          e.target.value,
                                          index,
                                          'OpexRate'
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      value={rate.EnerdsRate}
                                      onChange={(e) =>
                                        updateRateEntry(
                                          e.target.value,
                                          index,
                                          'EnerdsRate'
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="text"
                                      onChange={(e) =>
                                        updateRateEntry(
                                          e.target.value,
                                          index,
                                          'RateUnits'
                                        )
                                      }
                                      value={rate.RateUnits}
                                    />
                                  </td>
                                  <td>
                                    <Button
                                      color="danger"
                                      onClick={(e) => onDeleteRate(index)}
                                    >
                                      Delete
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr className="text-center">
                                <td colSpan={6}>No Usage Rates</td>
                              </tr>
                            )}
                            <tr>
                              <th>Rate Type</th>
                              <th>CAPEX Rate</th>
                              <th>OPEX Rate</th>
                              <th>ENERDS Rate</th>
                              <th>Rate Units</th>
                              <th></th>
                            </tr>
                            <tr>
                              <td>
                                <Select
                                  options={RATE_TYPES.map((rateType) => ({
                                    value: rateType.value,
                                    label: rateType.label,
                                  }))}
                                  menuPortalTarget={document.querySelector(
                                    'body'
                                  )}
                                  value={
                                    RATE_TYPES.filter(
                                      (rateType) =>
                                        rateType.value === newRate.RateType
                                    ).map((rateType) => ({
                                      value: rateType.value,
                                      label: rateType.label,
                                    }))[0]
                                  }
                                  onChange={(e) =>
                                    onNewRateChange(e.value, 'RateType')
                                  }
                                />
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  value={newRate.CapexRate}
                                  onChange={(e) =>
                                    onNewRateChange(e.target.value, 'CapexRate')
                                  }
                                />
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  value={newRate.OpexRate}
                                  onChange={(e) =>
                                    onNewRateChange(e.target.value, 'OpexRate')
                                  }
                                />
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  value={newRate.EnerdsRate}
                                  onChange={(e) =>
                                    onNewRateChange(
                                      e.target.value,
                                      'EnerdsRate'
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <Input
                                  type="text"
                                  onChange={(e) =>
                                    onNewRateChange(
                                      e.target.value,
                                      'RateUnits'
                                    )
                                  }
                                  value={newRate.RateUnits || ""}
                                />
                              </td>
                              <td align="right">
                                <Button
                                  color="success"
                                  block
                                  onClick={addProductRate}
                                >
                                  Add
                                </Button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>

                  <div className="text-lg-right">
                    <Button color="primary" onClick={saveProduct}>
                      Save Product
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        )}

      {
        // when data is loaded and product id is not valid show product does not exists error UI
        data && data.Product === null && formType === 'edit' && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">Error loading this page</h4>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      Product with ID {props.pathArg} does not exists
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(ProductForm);
